/**
 * Created by rhinov15 on 14/12/2016.
 *
 * State to drag a coating into scene, has to be dropped in an entity to be applied to that one
 *
 * Handle Entity :
 * -Room
 * -Wall
 *
 * @param coating coating struct from AsssteManager
 * @constructor
 */

import { DragComponentState } from "./DragComponentState";
import { ActionState, ActionStateEnum, FloorCoatingAreaState, IdleDecoration } from "..";
import { EntityManager } from "../../plan/EntityManager";
import { PlanManager } from "../../../../cocosPlan";
import { Coating, Commands, ComponentConstants, Entity, Events, eventsManager, Joinery, Listener, math, roomManager, SceneConstants, TechnicalElement, Wall } from "@rhinov/savane-js";

declare var cc: any;

export class DragCoatingState extends DragComponentState {

    state: number = ActionStateEnum.DragCoating;
    // Key pressed shortcuts
    rKeyPressed: boolean = false;
    fKeyPressed: boolean = false;
    shiftPressed: boolean = false;

    xKeyPressed : boolean= false;

    // Set wall direct to false first
    wallDirect: boolean = false;
    candidates: { entity: Entity; hangType: any; }[] = [];
    coating: any;
    coatingDrag: Listener | null;

    constructor(entity: Entity, coating: any) {
        super(entity, coating, false, [SceneConstants.EntityType.Wall, SceneConstants.EntityType.Room]);
    
        // Overload
        this.coating = this.component as any;
        

        // Used if the coating to apply changes
        this.coatingDrag = eventsManager.instance.addListener(Events.COATING_DRAG, (data: any) => {
            var dataStruct = data.userData;
            this.coating = dataStruct.coating;
        });
    }

    // Return global enum
        getAction() {
            return ActionStateEnum.DragCoating;
        }

        displayErrorHangType() {
            eventsManager.instance.dispatch(Events.DISPLAY_ALERT, { message: 'Ce matériau ne peut pas être appliqué sur cette surface' });
        }

        // Keep track of any key pressed while in this state for key shortcuts
        keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
            // Escape ==> operation cancelled
            if (keyCode == cc.KEY.escape) {
                // No selection
                PlanManager.getInstance().updateItemsSelected(null);
                // Back to IdleDecoration state
                eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);
                return new IdleDecoration();
            }

            // CTRL or W (mean apply to multiple walls one after another)
            if (keyCode == cc.KEY.ctrl || keyCode == cc.KEY.w) {
                this.ctrlPressed = true;
            }

            if (keyCode == cc.KEY.shift) {
                this.shiftPressed = true;
            }

            // R means apply to entire room
            if (keyCode == cc.KEY.r) {
                this.rKeyPressed = true;
            }

            // F means apply to entire floor
            if (keyCode == cc.KEY.f) {
                this.fKeyPressed = true;
            }

            // X means we don't check hangtype
            if (keyCode == cc.KEY.x) {
                this.xKeyPressed = true;
            }

            return null;
        }

        // Release key unpressed
        keyUnPressed(keyCode: number, ctrl?: boolean, maj?: boolean) : ActionState | null {
            // CTRL or W
            if (keyCode == cc.KEY.ctrl || keyCode == cc.KEY.w) {
                this.ctrlPressed = false;
            }

            if (keyCode == cc.KEY.shift) {
                this.shiftPressed = false;
            }

            // R
            if (keyCode == cc.KEY.r) {
                this.rKeyPressed = false;
            }

            // F
            if (keyCode == cc.KEY.f) {
                this.fKeyPressed = false;
            }

            // X
            if (keyCode == cc.KEY.x) {
                this.xKeyPressed = false;
            }

            return null;
        }

        moveMouseCursor(cursorPosition: any, test?: any, realPos?: any) : ActionState | null {
            super.moveMouseCursor(cursorPosition, test, realPos);
            if (this.ctrlPressed) {
                if (this.entityHovered !== null) {
                    this.unHighlightEntity(this.entityHovered);
                }
                if (!this.coating.hang.ceiling && !this.coating.hang.floor && !this.xKeyPressed) {
                    this.displayErrorHangType();
                }
                else {
                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.FloorCoatingArea);
                    return new FloorCoatingAreaState(this.coating, null, cursorPosition);
                }
            }

            return null;
        }

        // Called when a mouse button is released
        mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
            // Any entity hovered
            if (this.entityHovered !== null) {
                if (this.entityHovered.isTechnicalElementEntity() && this.entityHovered instanceof TechnicalElement && this.coating.isHpFloorGenerator) {
                    this.node!.removeFromParent();
                    EntityManager.getInstance().deleteNodeAndChild(this.entity);

                    eventsManager.instance.dispatch(Events.DISPLAY_ALERT, { message: 'Les matériaux FloorGenerator sont interdits sur les éléments techniques.' });

                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                    return new IdleDecoration();
                }

                // If not room or wall, do nothing
                if (!this.entityHovered.isWallEntity() && !this.entityHovered.isRoomEntity() &&
                    !this.entityHovered.isStaircaseEntity() && !this.entityHovered.isJoineryEntity() &&
                    !this.entityHovered.isTechnicalElementEntity() && !this.entityHovered.isGeometryPrimitiveEntity()) {
                    this.node!.removeFromParent();
                    EntityManager.getInstance().deleteNodeAndChild(this.entity);

                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                    return new IdleDecoration();
                }

                if (this.entityHovered instanceof Joinery && this.entityHovered.joineryType !== SceneConstants.JoineryType.opening) {
                    this.node!.removeFromParent();
                    EntityManager.getInstance().deleteNodeAndChild(this.entity);

                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                    return new IdleDecoration();
                }

                // If technical element and not radiator or beam or frame
                if (this.entityHovered.isTechnicalElementEntity() && this.entityHovered instanceof TechnicalElement && !(this.entityHovered.objectId === SceneConstants.TechnicalElementType.radiator ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.beam ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.frame ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.pole ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.rosette ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.guardrail ||
                    this.entityHovered.objectId === SceneConstants.TechnicalElementType.wallDecoration)) {
                    this.node!.removeFromParent();
                    EntityManager.getInstance().deleteNodeAndChild(this.entity);

                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                    return new IdleDecoration();
                }

                // Yes, unhighlight it
                this.unHighlightEntity(this.entityHovered);

                // Staircase
                if (this.entityHovered.isStaircaseEntity()) {
                    this.node!.removeFromParent();
                    EntityManager.getInstance().deleteNodeAndChild(this.entity);

                    eventsManager.instance.dispatch(Events.SHOW_CONTEXTUAL_MENU, { entity: this.entityHovered, component: this.coating, forceApply: this.xKeyPressed });
                    eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                    return new IdleDecoration();
                }

                // If the "r" and "f" key aren't pressed then apply the coating to a single entity
                if ((!this.rKeyPressed) && (!this.fKeyPressed)) {
                    if (this.entityHovered.isRoomEntity()) {
                        if (!this.coating.hang.ceiling && !this.coating.hang.floor && !this.xKeyPressed) {
                            this.displayErrorHangType();
                        }
                        else {
                            eventsManager.instance.dispatch(Events.SHOW_CONTEXTUAL_MENU, { entity: this.entityHovered, component: this.coating, forceApply: this.xKeyPressed });
                        }
                    }
                    else {
                        if (this.entityHovered.isWallEntity() && !this.coating.hang.wall && !this.xKeyPressed) {
                            this.displayErrorHangType();
                        }
                        else {
                            if (this.entityHovered instanceof Wall && this.entityHovered.isSpecialWall) {
                                this.wallDirect = !this.wallDirect;
                                this.appendCommand();
                            } else {
                                var coatings = this.entityHovered.getComponents(ComponentConstants.ComponentType.Coating) as Coating[];
                                var floorGeneratorApplied = false;
                                for (i = 0; i < coatings.length; ++i) {
                                    var coating = coatings[i];

                                    if (this.entityHovered instanceof Wall && coating.hangType === (this.wallDirect ? Coating.HangType.wallUndirect : Coating.HangType.wallDirect)) {
                                        if (coating.floorGeneratorSettings) {
                                            floorGeneratorApplied = true;
                                        }
                                        break;
                                    }
                                    else {
                                        if (coating.floorGeneratorSettings) {
                                            floorGeneratorApplied = true;
                                        }
                                        break;       
                                    }
                                }
                                if (floorGeneratorApplied) {
                                    if (this.entityHovered instanceof Wall) {
                                        eventsManager.instance.dispatch(Events.SHOW_CONTEXTUAL_MENU, { entity: this.entityHovered, component: this.coating, forceApply: this.xKeyPressed, wallDirect: !this.wallDirect });
                                    }
                                    else {
                                        eventsManager.instance.dispatch(Events.SHOW_CONTEXTUAL_MENU, { entity: this.entityHovered, component: this.coating, forceApply: this.xKeyPressed });
                                    }
                                } else {
                                    this.appendCommand();
                                }
                            }
                        }
                    }
                }
                else {
                    // Get hovered entity type
                    var entityType = this.entityHovered.entityType;
                    var i;
                    var room = roomManager.getRoomAtPosition(this.planPos, PlanManager.getInstance().world.currentScene!.currentFloor!);

                    // If not apply to entire floor then apply to entire room
                    if (!this.fKeyPressed) {
                        // If entity is a wall
                        switch (entityType) {
                            case SceneConstants.EntityType.Wall:
                                // Apply the coating to all the walls of the room
                                if (room) {
                                    // ALl room walls
                                    var roomWalls = room.walls;
                                    for (i = 0; i < roomWalls.length; i++) {
                                        if (!roomWalls[i].isSpecialWall) {
                                            this.entityHovered = roomWalls[i];

                                            if ((this.entityHovered as Wall).getHangTypeForRoom(room) === Coating.HangType.wallDirect) {
                                                this.wallDirect = false;
                                            }
                                            else {
                                                this.wallDirect = true;
                                            }

                                            this.appendCommand();
                                        }
                                    }

                                    // All non room walls
                                    var nonRoomedWalls = room.nonRoomedWalls;
                                    for (i = 0; i < nonRoomedWalls.length; i++) {
                                        if (!nonRoomedWalls[i].isSpecialWall) {
                                            this.entityHovered = nonRoomedWalls[i];
                                            this.wallDirect = true;
                                            this.appendCommand();

                                            this.wallDirect = false;
                                            this.appendCommand();
                                        }
                                    }
                                }
                                break;

                            case SceneConstants.EntityType.TechnicalElement:
                                if ((this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.radiator ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.beam ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.frame ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.pole ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.rosette ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.guardrail ||
                                    (this.entityHovered as TechnicalElement).objectId === SceneConstants.TechnicalElementType.wallDecoration) {
                                    if (room) {
                                        var floor = PlanManager.getInstance().world.currentScene!.currentFloor!;
                                        var techElements = floor.technicalElementsWithStaircases;
                                        for (i = 0; i < techElements.length; i++) {
                                            if (techElements[i].objectId === (this.entityHovered as TechnicalElement).objectId) {
                                                if (room === roomManager.getRoomAtPosition(techElements[i].position, floor)) {
                                                    this.entityHovered = techElements[i];
                                                    this.appendCommand();
                                                }
                                            }
                                        }
                                    }
                                }
                                break;
                        }
                    }
                    else {
                        // Apply to all floor
                        var j;
                        var floor = PlanManager.getInstance().world.currentScene!.currentFloor!;

                        // If applied to a room, then this is a caoting for the floor, paint all floors or rooms
                        switch (entityType) {
                            case SceneConstants.EntityType.Room:
                                // Apply the coating to all the floors of the rooms
                                var rooms = floor.rooms;

                                for (j = 0; j < rooms.length; j++) {
                                    this.entityHovered = rooms[j];
                                    this.appendCommand();
                                }
                                break;

                            case SceneConstants.EntityType.Wall:
                                // Apply the coating to all the floors of the rooms
                                var roomWalls: Wall[] = floor.walls;

                                for (j = 0; j < roomWalls.length; j++) {
                                    if (!roomWalls[j].isSpecialWall) {
                                        this.entityHovered = roomWalls[j];
                                        this.wallDirect = true;
                                        this.appendCommand();

                                        this.wallDirect = false;
                                        this.appendCommand();
                                    }
                                }

                                var nonRoomedWalls: Wall[] = floor.nonRoomedWalls;
                                for (j = 0; j < nonRoomedWalls.length; j++) {
                                    if (!nonRoomedWalls[j].isSpecialWall) {
                                        this.entityHovered = nonRoomedWalls[j];
                                        this.wallDirect = true;
                                        this.appendCommand();

                                        this.wallDirect = false;
                                        this.appendCommand();
                                    }
                                }
                                break;

                            case SceneConstants.EntityType.TechnicalElement:
                                var objectId = (this.entityHovered as TechnicalElement).objectId
                                if (objectId === SceneConstants.TechnicalElementType.radiator ||
                                    objectId === SceneConstants.TechnicalElementType.beam ||
                                    objectId === SceneConstants.TechnicalElementType.frame ||
                                    objectId === SceneConstants.TechnicalElementType.pole ||
                                    objectId === SceneConstants.TechnicalElementType.rosette ||
                                    objectId === SceneConstants.TechnicalElementType.guardrail ||
                                    objectId === SceneConstants.TechnicalElementType.wallDecoration) {
                                    var techElements = floor.technicalElementsWithStaircases;
                                    for (j = 0; j < techElements.length; j++) {
                                        if (techElements[j].objectId === objectId) {
                                            this.entityHovered = techElements[j];
                                            this.appendCommand();
                                        }
                                    }
                                }
                                break;
                        }
                    }
                }
                this.executeCommand();
            }

            // If shift not pressed, release the coating
            if (!this.shiftPressed) {
                this.node!.removeFromParent();
                EntityManager.getInstance().deleteNodeAndChild(this.entity);

                eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);

                return new IdleDecoration();
            }

            return (null);
        }

        executeCommand() {
            var changeCoatingsCommand = new Commands.ChangeCoatingsCommand(this.candidates, this.coating);
            // Execute the command
            PlanManager.getInstance().executeCommand(changeCoatingsCommand);
            this.candidates = [];
        }

        // Create a change coating command and execute it
        appendCommand() {
            // Create a change coating comman with the entity to paint, the coating and the apply mode
            var hangType = this.wallDirect ? Coating.HangType.wallUndirect : Coating.HangType.wallDirect;
            switch (this.entityHovered!.entityType) {
                case SceneConstants.EntityType.Staircase:
                    hangType = Coating.HangType.floor;
                    break;
                case SceneConstants.EntityType.Joinery:
                    hangType = Coating.HangType.joinery;
                    break;
                case SceneConstants.EntityType.TechnicalElement:
                case SceneConstants.EntityType.GeometryPrimitive:
                    hangType = Coating.HangType.technicalElement;
                    break;

                case SceneConstants.EntityType.Wall:
                    if (!this.coating.hang.wall && !this.xKeyPressed) {
                        this.displayErrorHangType();
                        return;
                    }
                    break;
            }
            this.candidates.push({ entity: this.entityHovered!, hangType: hangType });
        }

        // Set edition cursor for this state
        setEditionCursor() {
            if (this.entityHovered !== null) {
                // If room with no floor, can't apply coating, show a cursor not allowed
                /*if (this.entityHovered.entityType === SceneConstants.EntityType.Room && this.entityHovered.doesntNeedFloor()) {
                    document.getElementById('gameCanvas')!.style.cursor = 'not-allowed';
                }
                else {*/
                document.getElementById('gameCanvas')!.style.cursor = 'default';
                //}
            }
            else {
                document.getElementById('gameCanvas')!.style.cursor = 'default';
            }
        }

        // Highlight the entity hovered by the coating
        highlightEntity(entity: Entity) {
            // Get cocos entity graphical node
            var node = EntityManager.getInstance().getNode(entity);
            // Color
            var color;

            if (node === null) {
                return;
            }

            // New color location at AM coating root
            if (this.coating.colors !== undefined) {
                color = this.coating.colors.realColor;
            }
            else {
                color = new cc.color(255, 0, 255);
            }

            // Set coating depending on entity type
            switch (entity.entityType) {
                case SceneConstants.EntityType.Room:
                    if (!(entity as any).doesntNeedFloor()) {
                        node.setTempCoating(color);
                    }
                    break;

                case SceneConstants.EntityType.Wall:
                    if (!this.wallDirect) {
                        node.setTempCoating(color, null);
                    }
                    else {
                        node.setTempCoating(null, color);
                    }
                    node.calculateNeighboorWallValues(entity);
                    break;
            }
        }

        // Unhighlight the hovered entity
        unHighlightEntity(entity: Entity) {
            // Get cocos entity graphical node
            var node = EntityManager.getInstance().getNode(entity);

            if (node === null) {
                return;
            }

            //Remove the temporary coating from the entity
            switch (entity.entityType) {
                case SceneConstants.EntityType.Room:
                    node.setTempCoating(null);
                    break;

                case SceneConstants.EntityType.Wall:
                    node.setTempCoating(null, null);
                    node.calculateNeighboorWallValues(entity);
                    break;
            }
        }

        isDecorationState() {
            return (true);
        }

        // Object destruction
        dispose() {
            // Destroy temporary node
            this.node!.removeFromParent();
            // Destroy temporary entity
            EntityManager.getInstance().deleteNodeAndChild(this.entity);
            // Remove coating drag listener
            if (this.coatingDrag !== null) {
                eventsManager.instance.removeListener(this.coatingDrag!);
            }
        }
}
